<template>
    <div class="bento-layout" :class="isAlreadyScanned ? 'warning' : 'error'">
        <!-- Status & Regal Container -->
        <div class="bento-header">
            <div class="bento-status">
                <div class="status-label">Status</div>
                <div class="status-value">{{ isAlreadyScanned ? 'Warnung' : 'Fehler' }}</div>
            </div>
            <div class="bento-slot">
                <div class="slot-label">{{ articleLocation ? 'Regal' : 'SKU' }}</div>
                <div class="slot-value">{{ articleLocation ? formattedArticleLocation : articleSku }}</div>
            </div>
        </div>

        <!-- Preview Container - Shows Error or Preview Image -->
        <div class="bento-preview">
            <div class="preview-content preview-error">
                <!-- Show preview image if available and already scanned -->
                <template v-if="isAlreadyScanned && printPreview && isImageUrl(printPreview)">
                    <img :src="printPreview" alt="Preview" class="preview-img" />
                    <v-icon :size="32" :color="iconColor" class="shake-icon">{{ icon }}</v-icon>
                    <p>{{ title }}</p>
                </template>
                <!-- Show preview text if available and already scanned -->
                <template v-else-if="isAlreadyScanned && printPreview">
                    <v-icon :size="32" :color="iconColor" class="shake-icon">{{ icon }}</v-icon>
                    <p class="preview-text-content">{{ printPreview }}</p>
                    <p class="error-title">{{ title }}</p>
                </template>
                <!-- Show error icon and info -->
                <template v-else>
                    <v-icon :size="48" :color="iconColor" class="shake-icon">{{ icon }}</v-icon>
                    <p>{{ title }}</p>
                    <div class="article-info">
                        <div class="info-line">SKU: <strong>{{ articleSku }}</strong></div>
                        <div v-if="articleLocation" class="info-line">Regal: <strong>{{ formattedArticleLocation }}</strong></div>
                    </div>
                </template>
            </div>
        </div>

        <!-- Message Container -->
        <div class="bento-message">
            <v-icon color="white" size="20">{{ messageIcon }}</v-icon>
            <span>{{ actionMessage }}</span>
            <span class="countdown">{{ countdown }}s</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ErrorArticleState',
    props: {
        articleSku: {
            type: String,
            required: true
        },
        articleLocation: {
            type: String,
            default: ''
        },
        originalSlot: {
            type: String,
            default: ''
        },
        requiresExtraSlot: {
            type: Boolean,
            default: false
        },
        errorReason: {
            type: String,
            default: 'not_found'
        },
        countdown: {
            type: Number,
            default: 10
        },
        printPreview: {
            type: String,
            default: ''
        }
    },
    methods: {
        isImageUrl(str) {
            if (!str) return false;
            const isUrl = str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:image');
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
            const hasImageExtension = imageExtensions.some(ext => str.toLowerCase().includes(ext));
            return isUrl || hasImageExtension;
        }
    },
    computed: {
        isAlreadyScanned() {
            return this.errorReason === 'already_scanned';
        },
        icon() {
            if (this.isAlreadyScanned) {
                return 'mdi-alert';
            }
            return 'mdi-close-circle';
        },
        iconColor() {
            if (this.isAlreadyScanned) {
                return '#FF9800';
            }
            return '#F44336';
        },
        title() {
            if (this.isAlreadyScanned) {
                return 'Bereits gescannt!';
            }
            return 'Artikel nicht gefunden!';
        },
        messageIcon() {
            if (this.isAlreadyScanned) {
                return 'mdi-check-circle';
            }
            return 'mdi-barcode-scan';
        },
        actionMessage() {
            if (this.isAlreadyScanned) {
                return 'Artikel bereits eingelagert';
            }
            return 'Scannen Sie einen anderen Artikel';
        },
        formattedArticleLocation() {
            const slotName = this.articleLocation || '';

            if (slotName.startsWith('SPERRGUT_')) {
                const name = slotName.replace('SPERRGUT_', '');
                return name.replace(/_/g, ' ').toLowerCase()
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }

            return slotName;
        }
    }
};
</script>

<style scoped>
/* Bento Layout - Responsive Grid */
.bento-layout {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-height: 0;
    overflow: hidden;
    gap: 8px;
    padding: 8px;
    box-sizing: border-box;
    background: #ffffff !important;
}

/* Error Theme */
.bento-layout.error .bento-status,
.bento-layout.error .bento-slot {
    background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
}

.bento-layout.error .bento-message {
    background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
}

/* Warning Theme */
.bento-layout.warning .bento-status,
.bento-layout.warning .bento-slot {
    background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
}

.bento-layout.warning .bento-message {
    background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
}

/* Header: Status & Regal */
.bento-header {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    height: auto;
}

.bento-status,
.bento-slot {
    border-radius: 8px;
    padding: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: white;
    text-align: center;
}

.status-label,
.slot-label {
    font-size: clamp(8px, 2vw, 10px);
    opacity: 0.9;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-value {
    font-size: clamp(12px, 3.5vw, 16px);
    font-weight: bold;
    margin-top: 2px;
}

.slot-value {
    font-size: clamp(14px, 4vw, 20px);
    font-weight: bold;
    margin-top: 2px;
    word-break: break-word;
    line-height: 1;
}

/* Preview Container - Flexible */
.bento-preview {
    flex: 1;
    min-height: 0;
    background: white;
    border-radius: 8px;
    border: 2px solid #e0e0e0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

.preview-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px;
    gap: 12px;
}

.preview-error p {
    font-size: clamp(14px, 3.5vw, 18px);
    font-weight: bold;
    color: #333;
    margin: 0;
    text-align: center;
}

.preview-img {
    max-width: 100%;
    max-height: 60%;
    object-fit: contain;
    margin-bottom: 8px;
}

.preview-text-content {
    font-size: clamp(13px, 3vw, 16px);
    font-weight: 600;
    color: #666;
    margin: 0;
    text-align: center;
}

.error-title {
    font-size: clamp(12px, 2.8vw, 15px);
    margin-top: 4px;
}

.article-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: center;
}

.info-line {
    font-size: clamp(11px, 2.5vw, 14px);
    color: #666;
}

.info-line strong {
    color: #333;
}

/* Message Container */
.bento-message {
    flex-shrink: 0;
    border-radius: 8px;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: white;
    font-size: clamp(11px, 2.8vw, 14px);
    font-weight: 500;
}

.countdown {
    margin-left: auto;
    font-weight: bold;
    font-size: clamp(12px, 3vw, 15px);
}

.shake-icon {
    animation: shake 0.5s;
}

@keyframes shake {
    0%, 100% {
        transform: translateX(0);
    }
    25% {
        transform: translateX(-10px);
    }
    75% {
        transform: translateX(10px);
    }
}

/* Responsive adjustments */
@media (max-width: 480px) {
    .bento-layout {
        gap: 6px;
        padding: 6px;
    }

    .bento-header {
        gap: 6px;
    }

    .bento-status,
    .bento-slot {
        padding: 5px;
    }

    .preview-content {
        padding: 4px;
        gap: 8px;
    }

    .bento-message {
        padding: 6px;
        gap: 6px;
    }
}
</style>
